// 需要导入React相关类型
import { useState, useEffect } from 'react';

/**
 * PWA相关工具函数
 * 用于检测PWA模式并处理安全区域适配
 */

/**
 * 检测是否运行在PWA模式下
 * @returns {boolean} 是否为PWA模式
 */
export const isPWAMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // 检测 display-mode: standalone
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  
  // 检测 iOS Safari 的 standalone 模式
  const isIOSStandalone = (window.navigator as any).standalone === true;
  
  // 检测 Android Chrome 的 TWA 模式
  const isTWA = document.referrer.includes('android-app://');
  
  return isStandalone || isIOSStandalone || isTWA;
};

/**
 * 检测是否支持安全区域
 * @returns {boolean} 是否支持安全区域
 */
export const supportsSafeArea = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // 检测是否支持 env() 函数
  return CSS.supports('padding-top', 'env(safe-area-inset-top)');
};

/**
 * 获取安全区域顶部偏移量（像素值）
 * @returns {number} 顶部安全区域像素值，如果无法获取则返回0
 */
export const getSafeAreaInsetTop = (): number => {
  if (typeof window === 'undefined' || !supportsSafeArea()) {
    return isPWAMode() ? 20 : 0; // PWA模式下提供后备值
  }
  
  try {
    // 创建临时元素来测量安全区域
    const testElement = document.createElement('div');
    testElement.style.position = 'fixed';
    testElement.style.top = '0';
    testElement.style.left = '0';
    testElement.style.width = '1px';
    testElement.style.height = '1px';
    testElement.style.paddingTop = 'env(safe-area-inset-top)';
    testElement.style.visibility = 'hidden';
    testElement.style.pointerEvents = 'none';
    
    document.body.appendChild(testElement);
    const computedStyle = window.getComputedStyle(testElement);
    const paddingTop = parseInt(computedStyle.paddingTop, 10) || 0;
    document.body.removeChild(testElement);
    
    return paddingTop;
  } catch (error) {
    console.warn('Failed to get safe area inset top:', error);
    return isPWAMode() ? 20 : 0;
  }
};

/**
 * 生成包含安全区域的CSS值
 * @param baseValue 基础值（如 '56px'）
 * @param useMax 是否使用max函数确保最小值
 * @returns CSS值字符串
 */
export const getSafeAreaAwareCSS = (baseValue: string, useMax: boolean = false): string => {
  if (!supportsSafeArea()) {
    return isPWAMode() ? `calc(${baseValue} + 20px)` : baseValue;
  }
  
  if (useMax) {
    return `calc(${baseValue} + max(env(safe-area-inset-top), 20px))`;
  }
  
  return `calc(${baseValue} + env(safe-area-inset-top))`;
};

/**
 * 获取PWA安全区域相关的内联样式
 * @param includeTop 是否包含顶部安全区域
 * @param includeBottom 是否包含底部安全区域
 * @returns 样式对象
 */
export const getPWASafeAreaStyles = (includeTop: boolean = true, includeBottom: boolean = false) => {
  const styles: Record<string, string> = {};
  
  if (includeTop && isPWAMode()) {
    if (supportsSafeArea()) {
      styles.paddingTop = 'env(safe-area-inset-top)';
    } else {
      styles.paddingTop = '20px';
    }
  }
  
  if (includeBottom && isPWAMode()) {
    if (supportsSafeArea()) {
      styles.paddingBottom = 'env(safe-area-inset-bottom)';
    } else {
      styles.paddingBottom = '12px';
    }
  }
  
  return styles;
};

/**
 * React Hook - 检测PWA模式
 * @returns {boolean} 是否为PWA模式
 */
export const usePWAMode = (): boolean => {
  const [isPWA, setIsPWA] = useState(false);
  
  useEffect(() => {
    setIsPWA(isPWAMode());
    
    // 监听display-mode变化
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => setIsPWA(isPWAMode());
    
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);
  
  return isPWA;
}; 